import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import moment from "moment";
import "./CalendarPopover.css";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export interface CalendarPopoverHandle {
  open: () => Promise<{ startDate: string; endDate: string }>;
  close: () => void;
}

interface CalendarPopoverProps {
  onSave?: () => void;
}

interface MonthGridProps {
  year: number;
  month: number;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  onDayClick: (year: number, month: number, d: number) => void;
  minDate: moment.Moment;
}

function getDaysInMonth(year: number, month: number) {
  return moment({ year, month }).daysInMonth();
}

function getFirstDayOfMonth(year: number, month: number) {
  return moment({ year, month, day: 1 }).day();
}

function sameDay(a?: moment.Moment | null, b?: moment.Moment | null) {
  if (!a || !b) return false;
  return moment(a).isSame(b, "day");
}

function formatDate(date?: moment.Moment | null) {
  if (!date) return null;
  return moment(date).format("D MMM YYYY");
}

function MonthGrid({ year, month, startDate, endDate, onDayClick, minDate }: MonthGridProps) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthLabel = moment({ year, month }).format("MMMM");
  const today = moment();

  const cells: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const toDate = (d: number) => moment({ year, month, day: d });

  const isStart = (d: number | null) => d && sameDay(toDate(d), startDate);

  const isEnd = (d: number | null) => {
    if (!d || !startDate) return false;
    return endDate && sameDay(toDate(d), endDate) && !sameDay(toDate(d), startDate);
  };

  const isInRange = (d: number | null) => {
    if (!d || !startDate || !endDate) return false;

    const date = toDate(d);

    const from = moment.min(moment(startDate), moment(endDate));
    const to = moment.max(moment(startDate), moment(endDate));

    return date.isAfter(from, "day") && date.isBefore(to, "day");
  };

  const isToday = (d: number | null) => d && sameDay(toDate(d), today);

  const isDisabled = (d: number | null) => {
    if (!d || !minDate) return false;
    return toDate(d).isBefore(minDate, "day");
  };

  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  return (
    <div className="cal-month-section">
      <div className="cal-month-title">
        {monthLabel} {year}
      </div>

      <table className="cal-grid">
        <thead>
          <tr>
            {DAYS.map((d) => (
              <th key={d} className="cal-day-header">
                {d}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((d, di) => {
                const start = isStart(d);
                const end = isEnd(d);
                const inRange = isInRange(d);
                const disabled = isDisabled(d);

                let cellClass = "";

                if (start) cellClass += " range-start";
                if (end) cellClass += " range-end";
                if (inRange) cellClass += " in-range";

                if (
                  startDate &&
                  endDate &&
                  moment(startDate).isSame(endDate, "day")
                ) {
                  cellClass = cellClass
                    .replace(" range-start", "")
                    .replace(" range-end", "");
                }

                let dayClass = "cal-day";

                if (start || end) dayClass += " selected";
                if (isToday(d)) dayClass += " today";
                if (disabled) dayClass += " disabled";

                return (
                  <td key={di} className={`cal-cell ${cellClass}`}>
                    {d ? (
                      <div
                        className={dayClass}
                        onClick={() => !disabled && onDayClick(year, month, d)}
                      >
                        {d}
                      </div>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CalendarPopover = forwardRef<CalendarPopoverHandle, CalendarPopoverProps>(function CalendarPopover({ onSave }, ref) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [selectingEnd, setSelectingEnd] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);
  const callbackResolve = useRef<(result: { startDate: string; endDate: string }) => void>(() => undefined);

  const handleOpen = () => {
    setOpen(true);

    return new Promise<{ startDate: string; endDate: string }>((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const today = moment().startOf("day");

  const minDate = moment(today)
    .subtract(11, "months")
    .startOf("month");

  const months: { year: number; month: number }[] = [];

  for (let i = 11; i >= 0; i--) {
    const d = moment(today).subtract(i, "months");

    months.push({
      year: d.year(),
      month: d.month(),
    });
  }

  const handleDayClick = (year: number, month: number, d: number) => {
    const clicked = moment({ year, month, day: d });

    if (!selectingEnd) {
      setStartDate(clicked);
      setEndDate(null);
      setSelectingEnd(true);
    } else {
      // non-null: only reachable once startDate was set above (selectingEnd is only true after that)
      if (clicked.isBefore(startDate!, "day")) {
        setEndDate(startDate);
        setStartDate(clicked);
      } else {
        setEndDate(clicked);
      }

      setSelectingEnd(false);
    }
  };

  const handleClose = () => setOpen(false);

  const handleSave = () => {
    if (startDate && endDate) {
      callbackResolve.current({
        startDate: startDate.format("MM-DD-YYYY"),
        endDate: endDate.format("MM-DD-YYYY"),
      });

      handleClose();
    }
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (sheetRef.current) {
          sheetRef.current.scrollTop = sheetRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  if (!open) return null;

  return (
    <>
      <div className="cal-overlay open" onClick={handleClose} />

      <div className="cal-sheet open" ref={sheetRef}>
        <div className="cal-header">
          <div style={{ width: 16 }} />

          <div className="cal-range-display">
            <div className="cal-range-date">
              {formatDate(startDate) ?? "Start Date"}
            </div>

            <span className="cal-range-arrow">→</span>

            <div className="cal-range-date">
              {formatDate(endDate) ?? "End Date"}
            </div>
          </div>

          <button
            className="cal-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {months.map(({ year, month }, idx) => (
          <div key={`${year}-${month}`}>
            {idx > 0 && <hr className="cal-divider" />}

            <MonthGrid
              year={year}
              month={month}
              startDate={startDate}
              endDate={endDate}
              onDayClick={handleDayClick}
              minDate={minDate}
            />
          </div>
        ))}

        <div className="cal-footer">
          <button
            className="cal-save-btn"
            onClick={handleSave}
            disabled={!startDate || !endDate}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
});

export default CalendarPopover;
